import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ELEMENTS, Element as ElementType, TextPosition } from './infoData';
import ActionLinkButton from '../../UI/ActionLinkButton/ActionLinkButton';
import './bannerInfo.scss';

/* ==== Константы поведения ==== */
const FULLY_VISIBLE_TOLERANCE_PX = 2;
const AUTO_SCROLL_DURATION_MS = 650;
const LOCK_ON_FULL_VISIBILITY = true;

/* ==== Утилиты ==== */
function isFullyInViewport(el: HTMLElement): boolean {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top >= 0 + FULLY_VISIBLE_TOLERANCE_PX && r.bottom <= vh - FULLY_VISIBLE_TOLERANCE_PX;
}
function positionClasses(tp?: TextPosition) {
    if (!tp) return '';
    return `is-d-${tp.desctop} is-m-${tp.mobile}`;
}

/* Блокировка прокрутки страницы, кроме дозволенной области (текст активной секции) */
function usePageScrollLock(lock: boolean, allowedEl: HTMLElement | null) {
    useEffect(() => {
        if (!lock) return;

        const prevHtmlOverflow = document.documentElement.style.overflow;
        const prevBodyOverflow = document.body.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';

        const stopper = (e: Event) => {
            // Разрешаем скролл только внутри разрешённого элемента
            if (allowedEl && e.target instanceof Node && allowedEl.contains(e.target)) return;
            if ((e as any).cancelable) e.preventDefault();
        };

        document.addEventListener('wheel', stopper, { passive: false });
        document.addEventListener('touchmove', stopper, { passive: false });

        return () => {
            document.documentElement.style.overflow = prevHtmlOverflow;
            document.body.style.overflow = prevBodyOverflow;
            document.removeEventListener('wheel', stopper as any);
            document.removeEventListener('touchmove', stopper as any);
        };
    }, [lock, allowedEl]);
}

type BannerSectionProps = {
    elem: ElementType;
    index: number;
    isMobile?: boolean;
    setSectionRef: (i: number, el: HTMLElement | null) => void;
    setTextRef: (i: number, el: HTMLDivElement | null) => void;
    ioThreshold: number;
};

/* Секция-элемент */
const BannerSection: React.FC<BannerSectionProps> = ({ elem, index, isMobile, setSectionRef, setTextRef, ioThreshold }) => {
    const sectionElRef = useRef<HTMLElement | null>(null);

    // Добавляем/снимаем .is-visible для плавного появления
    useEffect(() => {
        const el = sectionElRef.current;
        if (!el) return;

        if (typeof IntersectionObserver === 'undefined') {
            el.classList.add('is-visible');
            return;
        }

        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) el.classList.add('is-visible');
                else el.classList.remove('is-visible');
            },
            { threshold: ioThreshold },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [ioThreshold]);

    const hasDesc = Boolean(elem.descImage);
    const hasFacts = Array.isArray(elem.facts) && elem.facts.length > 0;

    return (
        <section
            ref={(el) => {
                setSectionRef(index, el);
                sectionElRef.current = el;
            }}
            id={elem.elemKey}
            className={`bannerInfo__elem ${positionClasses(elem.textPosition)} ${hasDesc ? 'has-desc' : ''}`}
            aria-label={elem.title}
        >
            {/* Фон desktop / mobile */}
            <img
                className="bannerInfo__elem_bgImage bannerInfo__elem_bgImage--desktop"
                src={elem.backImage}
                alt=""
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
            />
            <img
                className="bannerInfo__elem_bgImage bannerInfo__elem_bgImage--mobile"
                src={elem.backImageMobile || elem.backImage}
                alt=""
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
            />

            {/* Полное затемнение (поверх фона, под контентом) */}
            <div className="bannerInfo__elem_overlay" aria-hidden="true" />

            {/* Контент */}
            <div className="bannerInfo__elem_contentWrap">
                <div className="bannerInfo__elem_content">
                    <h3 className="bannerInfo__elem_title">{elem.title}</h3>

                    <div className="bannerInfo__elem_textWrap" ref={(el) => setTextRef(index, el)} tabIndex={0} aria-label={`Текст раздела: ${elem.title}`}>
                        {/* Журнальное обтекание: descImage внутри текста с float */}
                        {hasFacts && (
                            <ul className="bannerInfo__elem_facts" aria-label="Ключевые свойства">
                                {elem.facts!.map((f, i) => (
                                    <li key={`fact-${i}`} className="bannerInfo__elem_factChip">
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {hasDesc && (
                            <figure className="bannerInfo__elem_descCard">
                                <img className="bannerInfo__elem_descImage" src={elem.descImage!} alt="" loading="lazy" decoding="async" />
                            </figure>
                        )}

                        {elem.text.map((t, i) => (
                            <p key={`p-${i}`} className="bannerInfo__elem_paragraph">
                                {t}
                            </p>
                        ))}

                        {/* clearfix для корректного окончания обтекания перед кнопкой */}
                        <div className="bannerInfo__elem_textClear" aria-hidden="true" />
                    </div>

                    {elem.link?.url && (
                        // <a className="bannerInfo__elem_link" href={elem.link.url}>
                        //     {elem.link.text ?? 'Подробнее'}
                        // </a>
                        <ActionLinkButton
                            to={elem.link.url}
                            iconSrc="/icons/mobile/external-link.svg"
                            iconSrcDark="/icons/external-link_w.svg"
                            iconPosition="end"
                            text={elem.link.text ?? 'Подробнее'}
                            className="bannerInfo__elem_linkMob"
                        />
                    )}
                </div>
            </div>
        </section>
    );
};

type BannerInfoProps = {
    logSampleSize?: number;
    isMobile?: boolean;
};

const BannerInfo: React.FC<BannerInfoProps> = ({ isMobile }) => {
    const items = useMemo(() => ELEMENTS ?? [], []);
    const sectionRefs = useRef<Array<HTMLElement | null>>([]);
    const textRefs = useRef<Array<HTMLDivElement | null>>([]);

    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [lockPage, setLockPage] = useState(false);
    const [autoScrolling, setAutoScrolling] = useState(false);

    const setSectionRef = useCallback((i: number, el: HTMLElement | null) => {
        sectionRefs.current[i] = el;
    }, []);
    const setTextRef = useCallback((i: number, el: HTMLDivElement | null) => {
        textRefs.current[i] = el;
    }, []);

    const getIoThreshold = useCallback(
        (i: number): number => {
            if (i === 0) return 0.3;
            if (i === items.length - 1) return 0.2;
            return 0.3;
        },
        [items.length],
    );

    /* Лочим страницу, разрешая скроллТолько в тексте активной секции */
    usePageScrollLock(lockPage, activeIndex !== null ? (textRefs.current[activeIndex] ?? null) : null);

    /* Поиск секции, полностью видимой во вьюпорте */
    useEffect(() => {
        if (!items.length) return;

        let raf = 0;
        const onScrollOrResize = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                if (autoScrolling) return;

                let fullIdx: number | null = null;
                for (let i = 0; i < sectionRefs.current.length; i++) {
                    const el = sectionRefs.current[i];
                    if (el && isFullyInViewport(el)) {
                        fullIdx = i;
                        break;
                    }
                }

                if (fullIdx === null) {
                    if (lockPage) setLockPage(false);
                    if (activeIndex !== null) setActiveIndex(null);
                    return;
                }

                if (activeIndex !== fullIdx) setActiveIndex(fullIdx);

                const textEl = textRefs.current[fullIdx];
                if (!textEl) {
                    if (lockPage) setLockPage(false);
                    return;
                }
                const scrollable = textEl.scrollHeight > textEl.clientHeight + 1;

                if (LOCK_ON_FULL_VISIBILITY && scrollable) {
                    if (!lockPage) {
                        setLockPage(true);
                        setTimeout(() => textEl.focus({ preventScroll: true }), 0);
                    }
                } else {
                    if (lockPage) setLockPage(false);
                }
            });
        };

        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize, { passive: true });
        onScrollOrResize();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('scroll', onScrollOrResize as any);
            window.removeEventListener('resize', onScrollOrResize as any);
        };
    }, [items.length, activeIndex, lockPage, autoScrolling]);

    /* Навигация к соседним секциям на краях текста (desktop wheel + mobile touch + клавиши) */
    useEffect(() => {
        if (!lockPage || activeIndex === null) return;
        const el = textRefs.current[activeIndex];
        if (!el) return;

        let touchStartY = 0;

        const atTop = () => el.scrollTop <= 0;
        const atBottom = () => Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;

        const toPrev = () => {
            const prevEl = sectionRefs.current[activeIndex - 1];
            if (!prevEl) return;
            setAutoScrolling(true);
            setLockPage(false);
            prevEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(() => setAutoScrolling(false), AUTO_SCROLL_DURATION_MS);
        };

        const toNext = () => {
            const nextEl = sectionRefs.current[activeIndex + 1];
            if (!nextEl) return;
            setAutoScrolling(true);
            setLockPage(false);
            nextEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(() => setAutoScrolling(false), AUTO_SCROLL_DURATION_MS);
        };

        // даём браузеру нативно скроллить внутри текста.
        // Перехватываем только попытки «выйти» за края, чтобы перейти к соседней секции.
        const onWheel = (e: WheelEvent) => {
            const dy = e.deltaY;
            if (dy < 0 && atTop()) {
                e.preventDefault();
                toPrev();
            } else if (dy > 0 && atBottom()) {
                e.preventDefault();
                toNext();
            }
        };

        const onTouchStart = (e: TouchEvent) => {
            touchStartY = e.touches[0]?.clientY ?? 0;
        };
        const onTouchMove = (e: TouchEvent) => {
            const y = e.touches[0]?.clientY ?? 0;
            const dy = touchStartY - y;

            // На iOS/Safari оставляем нативный скролл — вмешиваемся только на краях
            if (dy < -6 && atTop()) {
                e.preventDefault();
                toPrev();
            } else if (dy > 6 && atBottom()) {
                e.preventDefault();
                toNext();
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'PageDown' || e.key === 'ArrowDown') {
                if (atBottom()) {
                    e.preventDefault();
                    toNext();
                }
            } else if (e.key === 'PageUp' || e.key === 'ArrowUp') {
                if (atTop()) {
                    e.preventDefault();
                    toPrev();
                }
            }
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('keydown', onKeyDown);

        // Фокус в текст — для доступности
        setTimeout(() => el.focus({ preventScroll: true }), 0);

        return () => {
            el.removeEventListener('wheel', onWheel as any);
            el.removeEventListener('touchstart', onTouchStart as any);
            el.removeEventListener('touchmove', onTouchMove as any);
            document.removeEventListener('keydown', onKeyDown as any);
        };
    }, [lockPage, activeIndex]);

    if (!items.length) return null;

    return (
        <div className="bannerInfo" role="region" aria-label="Информационные баннеры">
            {items.map((elem, index) => (
                <BannerSection
                    key={`info-${elem.elemKey}-${index}`}
                    elem={elem}
                    index={index}
                    isMobile={isMobile}
                    setSectionRef={setSectionRef}
                    setTextRef={setTextRef}
                    ioThreshold={getIoThreshold(index)}
                />
            ))}
        </div>
    );
};

export default BannerInfo;
